import Image from '@atoms/Image/Image';
import LinkCard from '@atoms/LinkCard/LinkCard';
import LoadMoreButton from '@atoms/LoadMoreButton/LoadMoreButton';
import { IKitBag } from '@models/IKitBag';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useRefocus from '@utils/hooks/useRefocus';
import classNames from 'classnames';
import { useState } from 'react';

const KitBag = ({ title, loadMoreLabel, loadLessLabel, items }: IKitBag) => {
  const [open, setOpen] = useState(false);
  const isMobile = useMediaQuery(1440);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const focusPlaceholderRef = useRefocus<HTMLAnchorElement>(open);

  return (
    <section className="overflow-hidden text-black-100 dark:text-white">
      {title && (
        <div className="body-one container text-black-100 dark:text-grey-400 xl:mx-0 xl:max-w-none xl:px-0">
          {title}
        </div>
      )}

      {items && !!items.length && (
        <div className="mt-6 flex w-full flex-col overflow-hidden xl:mt-4">
          <div className="relative w-full snap-x overflow-x-auto scrollbar-hide">
            <div className="flex w-full flex-row overflow-visible pl-5 md:px-15 lg:px-24 xl:flex-col xl:gap-5 xl:px-0">
              {items
                .slice(
                  0,
                  isMobile || open || !loadMoreLabel || !loadLessLabel
                    ? items.length
                    : 3
                )
                .map((item, index) => (
                  <div
                    // eslint-disable-next-line react/no-unknown-property
                    track-product-impression="true"
                    data-product={JSON.stringify(item.trackingData ?? '')}
                    key={item.name}
                    className={classNames(
                      'h-auto shrink-0 snap-center xl:h-36 xl:w-full xl:pr-0',
                      items.length === 1 ? 'w-full' : 'max-w-[80vw]',
                      index + 1 === items.length
                        ? 'pr-5 md:pr-15 lg:pr-24'
                        : 'pr-4'
                    )}
                  >
                    <LinkCard
                      link={item.link}
                      ref={
                        focusedIndex > 0 && focusedIndex === index
                          ? focusPlaceholderRef
                          : null
                      }
                    >
                      <div className="flex h-full flex-row items-center gap-5 overflow-hidden overflow-x-hidden px-5 py-4 xl:gap-8 xl:px-8 2xl:gap-11 2xl:pl-11">
                        {item.thumbnail && (
                          <div className="flex h-full w-[70px] shrink-0 justify-center md:w-[120px]">
                            <Image
                              image={item.thumbnail}
                              imageClasses="h-full object-contain"
                              sizes="300px"
                            />
                          </div>
                        )}

                        <div className="body-one flex flex-col">
                          {item.name && (
                            <span className="font-normal text-black-100 dark:font-light dark:text-yellow">
                              {item.name}
                            </span>
                          )}
                          {item.description && <span>{item.description}</span>}
                        </div>
                      </div>
                    </LinkCard>
                  </div>
                ))}
              <div className="block h-full w-5" />
            </div>

            <div
              className={classNames(
                'pointer-events-none absolute bottom-0 left-0 z-10 hidden h-36 w-full bg-gradient-to-t from-white to-transparent dark:from-black-100',
                {
                  'xl:block':
                    !open && loadMoreLabel && loadLessLabel && items.length > 3,
                }
              )}
            />
          </div>

          {items.length > 3 && loadMoreLabel && loadLessLabel && !isMobile && (
            <LoadMoreButton
              loadLessLabel={loadLessLabel}
              loadMoreLabel={loadMoreLabel}
              open={open}
              onClick={() => {
                setOpen(!open);
                setFocusedIndex(open ? 2 : 3);
              }}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default KitBag;
